import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "system";
export type ColorTheme = "flowstate-blue" | "purple-edge" | "emerald" | "amber" | "rose";
export type GlowIntensity = "subtle" | "normal" | "intense" | "max";

const COLOR_THEMES: ColorTheme[] = ["flowstate-blue", "purple-edge", "emerald", "amber", "rose"];
const VALID_THEMES: Theme[] = ["light", "dark", "system"];
const VALID_GLOW_INTENSITIES: GlowIntensity[] = ["subtle", "normal", "intense", "max"];

export const GLOW_MULTIPLIERS: Record<GlowIntensity, number> = {
  subtle: 0.5,
  normal: 1.0,
  intense: 1.5,
  max: 2.0,
};

type ThemeProviderState = {
  theme: Theme;
  /** `theme` resolved to a concrete value — "system" becomes the OS preference. */
  resolvedTheme: "light" | "dark";
  colorTheme: ColorTheme;
  glowIntensity: GlowIntensity;
  setTheme: (theme: Theme) => void;
  setColorTheme: (colorTheme: ColorTheme) => void;
  setGlowIntensity: (intensity: GlowIntensity) => void;
};

// null sentinel forces useTheme() to throw when called outside a ThemeProvider,
// rather than silently returning placeholder state.
const ThemeProviderContext = createContext<ThemeProviderState | null>(null);

const STORAGE_KEY = "flowstate-ui-theme";
const COLOR_THEME_KEY = "flowstate-ui-color-theme";
const GLOW_KEY = "flowstate-ui-glow-intensity";

export function ThemeProvider({
  children,
  defaultTheme = "system",
}: {
  children: ReactNode;
  defaultTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
      if (stored && VALID_THEMES.includes(stored)) return stored;
    }
    return defaultTheme;
  });

  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(COLOR_THEME_KEY) as ColorTheme | null;
      if (stored && COLOR_THEMES.includes(stored)) return stored;
    }
    return "flowstate-blue";
  });

  const [glowIntensity, setGlowIntensityState] = useState<GlowIntensity>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(GLOW_KEY) as GlowIntensity | null;
      if (stored && VALID_GLOW_INTENSITIES.includes(stored)) return stored;
    }
    return "normal";
  });

  // Resolved OS preference — tracked in state so "system" mode reacts live
  // when the user flips their OS theme while the app is open.
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = window.document.documentElement;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!reduceMotion) root.classList.add("theme-transitioning");
    // Remove all recognised theme state classes before adding the resolved one
    root.classList.remove("light", "dark", "system");
    root.classList.add(theme === "system" ? systemTheme : theme);
    const timer = reduceMotion ? undefined : window.setTimeout(() => root.classList.remove("theme-transitioning"), 350);
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, [theme, systemTheme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = window.document.documentElement;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!reduceMotion) root.classList.add("color-theme-transitioning");
    COLOR_THEMES.forEach((t) => root.classList.remove(`theme-${t}`));
    root.classList.add(`theme-${colorTheme}`);
    const timer = reduceMotion ? undefined : window.setTimeout(() => root.classList.remove("color-theme-transitioning"), 1200);
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, [colorTheme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = window.document.documentElement;
    root.style.setProperty("--glow-multiplier", GLOW_MULTIPLIERS[glowIntensity].toString());
  }, [glowIntensity]);

  // Cross-tab sync: pick up theme changes made in other browser tabs
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && VALID_THEMES.includes(e.newValue as Theme)) {
        setThemeState(e.newValue as Theme);
      }
      if (e.key === COLOR_THEME_KEY && e.newValue && COLOR_THEMES.includes(e.newValue as ColorTheme)) {
        setColorThemeState(e.newValue as ColorTheme);
      }
      if (e.key === GLOW_KEY && e.newValue && VALID_GLOW_INTENSITIES.includes(e.newValue as GlowIntensity)) {
        setGlowIntensityState(e.newValue as GlowIntensity);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setTheme = (newTheme: Theme) => {
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, newTheme);
    setThemeState(newTheme);
  };

  const setColorTheme = (newColorTheme: ColorTheme) => {
    if (typeof window !== "undefined") localStorage.setItem(COLOR_THEME_KEY, newColorTheme);
    setColorThemeState(newColorTheme);
  };

  const setGlowIntensity = (newGlow: GlowIntensity) => {
    if (typeof window !== "undefined") localStorage.setItem(GLOW_KEY, newGlow);
    setGlowIntensityState(newGlow);
  };

  const resolvedTheme = theme === "system" ? systemTheme : theme;

  return (
    <ThemeProviderContext.Provider
      value={{ theme, resolvedTheme, colorTheme, glowIntensity, setTheme, setColorTheme, setGlowIntensity }}
    >
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);
  if (context === null) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
